import { getFacetAttributeName } from "@/components/Search/utils/getFacetAttributeName";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import type {
  RelatedProductsFilter,
  RelatedProductsSource,
} from "@/monarch/types/ProductCompare";

type CompareFilterSubjects =
  | ({ name?: string | null } | string)[]
  | null
  | undefined;

export type CompareFilterSubjectSource = {
  subjects?: CompareFilterSubjects;
  subjectEn?: string | null;
  subjectEs?: string | null;
};

export type CompareFilterProduct = CompareFilterSubjectSource & {
  productUuid?: string | null;
  partners?: { name?: string | null }[] | null;
  skills?:
    | {
        skill?: string | null;
        category?: string | null;
      }[]
    | null;
};

/** Program/course shape used for in-memory compare overlap matching. */
export type CompareFilterProgram = CompareFilterSubjectSource & {
  partners?: { name?: string | null }[] | null;
  skills?:
    | {
        skill?: string | null;
        category?: string | null;
      }[]
    | null;
};

function collectNamedSubjects(subjects: CompareFilterSubjects): string[] {
  return (subjects ?? [])
    .map((subject) => {
      if (typeof subject === "string") return subject;
      return subject?.name ?? null;
    })
    .filter((name): name is string => Boolean(name));
}

/** Subject names for overlap matching; mirrors collectSubjectFilters fallback rules. */
export function collectCompareSubjectNames(
  product: CompareFilterSubjectSource,
): Set<string> {
  const named = collectNamedSubjects(product.subjects);

  if (named.length > 0) return new Set(named);

  const fallback = product.subjectEn || product.subjectEs;
  return fallback ? new Set([fallback]) : new Set();
}

export const DEFAULT_COURSE_RELATED_PRODUCTS_FILTERS: RelatedProductsFilter[] =
  ["sameBrand", "subject", "skill"];

export const DEFAULT_PROGRAM_RELATED_PRODUCTS_FILTERS: RelatedProductsFilter[] =
  ["sameBrand"];

export function resolveRelatedProductsFilters(
  configured: RelatedProductsFilter[] | undefined,
  productContext: "course" | "program",
  source: RelatedProductsSource = "courses",
): RelatedProductsFilter[] {
  if (configured?.length) {
    return configured.includes("sameBrand")
      ? configured
      : ["sameBrand", ...configured];
  }

  if (productContext === "program" || source === "programs") {
    return DEFAULT_PROGRAM_RELATED_PRODUCTS_FILTERS;
  }

  return DEFAULT_COURSE_RELATED_PRODUCTS_FILTERS;
}

/** Stable memo key for resolved filter lists (avoids new-array reference churn). */
export function getRelatedProductsFiltersKey(
  configured: RelatedProductsFilter[] | undefined,
  productContext: "course" | "program",
  source: RelatedProductsSource = "courses",
): string {
  return resolveRelatedProductsFilters(configured, productContext, source).join(
    "|",
  );
}

/** Stable memo key for partner name lists passed into Algolia refinements. */
export function getPartnerNamesKey(partnerNames: string[]): string {
  return [...partnerNames].sort().join("|");
}

/** Stable memo key for inline `product={{...}}` props from templates. */
export function getCompareFilterProductKey(
  product: CompareFilterProduct | undefined,
): string {
  if (!product) return "";

  const partners = (product.partners ?? [])
    .map((partner) => partner?.name ?? "")
    .filter(Boolean)
    .sort()
    .join(",");

  const subjects = [...collectNamedSubjects(product.subjects)].sort().join(",");

  const skills = (product.skills ?? [])
    .map((entry) => `${entry?.skill ?? ""}:${entry?.category ?? ""}`)
    .filter((entry) => entry !== ":")
    .sort()
    .join(",");

  return [
    product.productUuid ?? "",
    partners,
    subjects,
    product.subjectEn ?? "",
    product.subjectEs ?? "",
    skills,
  ].join("|");
}

export function collectSubjectFilters(
  product: CompareFilterProduct | undefined,
  indexName: string = PRODUCT_INDEX_EN as string,
): string[] {
  if (!product) return [];

  const facet = getFacetAttributeName("subject", { indexName });
  const named = collectNamedSubjects(product.subjects);

  if (named.length > 0) return named.map((name) => `${facet}:"${name}"`);

  const fallback = product.subjectEn || product.subjectEs;
  return fallback ? [`${facet}:"${fallback}"`] : [];
}

export function collectSkillFilters(
  product: CompareFilterProduct | undefined,
  indexName: string = PRODUCT_INDEX_EN as string,
): string[] {
  if (!product) return [];

  const facet = getFacetAttributeName("skills.skill", { indexName });
  return (product.skills ?? [])
    .map((entry) => entry?.skill)
    .filter((skill): skill is string => Boolean(skill))
    .map((skill) => `${facet}:"${skill}"`);
}

export function collectCategoryFilters(
  product: CompareFilterProduct | undefined,
): string[] {
  if (!product) return [];

  const categories = new Set(
    (product.skills ?? [])
      .map((entry) => entry?.category)
      .filter((category): category is string => Boolean(category)),
  );

  // Product index records may store category on either attribute (see CrossSell).
  return [...categories].flatMap((category) => [
    `skills.category:"${category}"`,
    `skillsData.category:"${category}"`,
  ]);
}

export function collectPartnerFilters(
  product: CompareFilterProduct | undefined,
  indexName: string = PRODUCT_INDEX_EN as string,
): string[] {
  if (!product) return [];

  const facet = getFacetAttributeName("partner", { indexName });
  return (product.partners ?? [])
    .map((partner) => partner?.name)
    .filter((name): name is string => Boolean(name))
    .map((name) => `${facet}:"${name}"`);
}

export function buildOverlapFilters(
  enabledFilters: RelatedProductsFilter[],
  product: CompareFilterProduct | undefined,
  indexName: string = PRODUCT_INDEX_EN as string,
): string[] {
  const overlap: string[] = [];

  if (enabledFilters.includes("subject")) {
    overlap.push(...collectSubjectFilters(product, indexName));
  }
  if (enabledFilters.includes("skill")) {
    overlap.push(...collectSkillFilters(product, indexName));
  }
  if (enabledFilters.includes("category")) {
    overlap.push(...collectCategoryFilters(product));
  }

  return overlap;
}

export function buildCompareFacetFilters({
  currentUuid,
  enabledFilters,
  product,
  indexName = PRODUCT_INDEX_EN as string,
}: {
  currentUuid: string;
  enabledFilters: RelatedProductsFilter[];
  product?: CompareFilterProduct;
  indexName?: string;
}): (string | string[])[] {
  const filters: (string | string[])[] = [];
  const overlapDisjunction = buildOverlapFilters(
    enabledFilters,
    product,
    indexName,
  );

  if (overlapDisjunction.length > 0) {
    filters.push(overlapDisjunction);
  }

  if (enabledFilters.includes("sameBrand")) {
    const partnerFilters = collectPartnerFilters(product, indexName);
    if (partnerFilters.length > 0) filters.push(partnerFilters);
  }

  const uuidFacet = getFacetAttributeName("productUuid", { indexName });
  filters.push(`${uuidFacet}:-${currentUuid}`);

  return filters;
}

export function canFetchRelatedProducts({
  product,
  enabledFilters,
}: {
  product?: CompareFilterProduct;
  enabledFilters: RelatedProductsFilter[];
}): boolean {
  const normalizedUuid = product?.productUuid?.replace(
    /^course-|^program-/,
    "",
  );
  if (!normalizedUuid) return false;

  if (enabledFilters.includes("sameBrand")) {
    if (collectPartnerFilters(product).length === 0) return false;
  }

  const overlapEnabled = enabledFilters.some((filter) =>
    ["subject", "skill", "category"].includes(filter),
  );

  if (
    overlapEnabled &&
    buildOverlapFilters(enabledFilters, product).length === 0
  ) {
    return false;
  }

  return true;
}

export function programMatchesCompareFilters(
  program: CompareFilterProgram,
  currentProduct: CompareFilterProduct,
  enabledFilters: RelatedProductsFilter[],
): boolean {
  if (enabledFilters.includes("sameBrand")) {
    const currentPartners = collectPartnerNames(currentProduct);
    if (currentPartners.size === 0) return false;

    const sharesPartner = (program.partners ?? []).some(
      (partner) => partner?.name && currentPartners.has(partner.name),
    );
    if (!sharesPartner) return false;
  }

  const overlapFilters = enabledFilters.filter((filter) =>
    ["subject", "skill", "category"].includes(filter),
  );

  if (overlapFilters.length === 0) return true;

  const currentSubjects = collectCompareSubjectNames(currentProduct);
  const programSubjects = collectCompareSubjectNames(program);

  const currentSkills = new Set(
    (currentProduct.skills ?? [])
      .map((skill) => skill?.skill)
      .filter((skill): skill is string => Boolean(skill)),
  );
  const currentCategories = new Set(
    (currentProduct.skills ?? [])
      .map((skill) => skill?.category)
      .filter((category): category is string => Boolean(category)),
  );

  const programSkills = new Set(
    (program.skills ?? [])
      .map((skill) => skill?.skill)
      .filter((skill): skill is string => Boolean(skill)),
  );
  const programCategories = new Set(
    (program.skills ?? [])
      .map((skill) => skill?.category)
      .filter((category): category is string => Boolean(category)),
  );

  const matches: boolean[] = [];

  if (overlapFilters.includes("subject")) {
    matches.push(
      [...currentSubjects].some((subject) => programSubjects.has(subject)),
    );
  }
  if (overlapFilters.includes("skill")) {
    matches.push([...currentSkills].some((skill) => programSkills.has(skill)));
  }
  if (overlapFilters.includes("category")) {
    matches.push(
      [...currentCategories].some((category) =>
        programCategories.has(category),
      ),
    );
  }

  return matches.some(Boolean);
}

function collectPartnerNames(product: CompareFilterProduct): Set<string> {
  return new Set(
    (product.partners ?? [])
      .map((partner) => partner?.name)
      .filter((name): name is string => Boolean(name)),
  );
}
